/**
 * 路由表。
 * 页面一律懒加载：画布与图表分别拖着 reactflow / recharts，
 * 全量打进首屏会让「打开列表页」也要等这两个包。
 */

import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { Skeleton } from 'antd';
import { MainLayout } from '@/layouts/MainLayout';
import { RequireAuth } from '@/layouts/RequireAuth';
import { NotFound } from '@/pages/NotFound';

const Login = lazy(() => import('@/pages/Login'));
const ApiDocs = lazy(() => import('@/pages/ApiDocs'));
const SharedRun = lazy(() => import('@/pages/SharedRun'));
const HomeGate = lazy(() => import('@/pages/HomeGate'));
const UserSettings = lazy(() => import('@/pages/UserSettings'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const WorkflowList = lazy(() => import('@/pages/WorkflowList'));
const WorkflowDetail = lazy(() => import('@/pages/WorkflowDetail'));
const RunHistory = lazy(() => import('@/pages/RunHistory'));
const RunDetail = lazy(() => import('@/pages/RunDetail'));
const CredentialSettings = lazy(() => import('@/pages/CredentialSettings'));
const ModelProviderSettings = lazy(() => import('@/pages/ModelProviderSettings'));
const ScheduleSettings = lazy(() => import('@/pages/ScheduleSettings'));
const OpsPanel = lazy(() => import('@/pages/OpsPanel'));

export const router = createBrowserRouter([
  {
    // 登录页在守卫**之外**，也不套 MainLayout：未登录的人不该看到侧栏导航，
    // 更不该让 MainLayout 里的健康轮询在登录前就开始打接口。
    path: '/login',
    element: (
      <Suspense fallback={<Skeleton active style={{ padding: 24 }} />}>
        <Login />
      </Suspense>
    ),
  },
  {
    // 面向接入方的公开中文文档。首页、登录页都会链接到这里，不能放进登录守卫。
    path: '/docs',
    element: (
      <Suspense fallback={<Skeleton active style={{ padding: 24 }} />}>
        <ApiDocs />
      </Suspense>
    ),
  },
  {
    // 公开运行页。**在守卫之外，也不套 MainLayout** —— 收到链接的人不该看到
    // 侧栏导航、运行历史、设置入口，那些都是内部界面。
    // 路径用 /s/ 而不是 /share/，短一点，链接发出去好看。
    path: '/s/:token',
    element: (
      <Suspense fallback={<Skeleton active style={{ padding: 24 }} />}>
        <SharedRun />
      </Suspense>
    ),
  },
  {
    // 首页在守卫之外。已登录的人访问 `/` 会被 `<HomeGate>` 直接送去控制台 ——
    // 每天从书签点进来的人不该多点一次"进入控制台"。
    path: '/',
    element: (
      <Suspense fallback={<Skeleton active style={{ padding: 24 }} />}>
        <HomeGate />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <MainLayout />
      </RequireAuth>
    ),
    children: [
      { path: 'dashboard', element: <Dashboard /> },
      { path: 'workflows', element: <WorkflowList /> },
      { path: 'workflows/:wfId', element: <WorkflowDetail /> },
      { path: 'runs', element: <RunHistory /> },
      { path: 'runs/:runId', element: <RunDetail /> },
      { path: 'settings/credentials', element: <CredentialSettings /> },
      { path: 'settings/model-providers', element: <ModelProviderSettings /> },
      { path: 'settings/schedules', element: <ScheduleSettings /> },
      { path: 'settings/users', element: <UserSettings /> },
      { path: 'ops', element: <OpsPanel /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
